import useMediaQuery from '@package/hooks/src/useMediaQuery';
import useSearch from '@package/hooks/src/useSearch';
import { FormErrorCode, isEnglishAlphabet } from '@package/utils/src/formValidation';
import useClickOutSide from '@package/utils/src/useClickOutside';
import useDebounce from '@package/utils/src/useDebounce';
import clsx from 'clsx';
import config from 'config-base';
import dynamic from 'next/dynamic';
import { memo, useEffect, useRef, useState } from 'react';
import styles from './SearchComponent.module.css';
import { SearchComponentProps } from './types';
const SearchResults = dynamic(() => import('./__components/SearchResult'));

const SearchComponent = ({
  handleSearchPosition,
  topPosition,
  isMenuOpen,
  handleInputFocus,
  id = 'serch-field',
}: SearchComponentProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [isSearchResultOpened, setIsSearchResultOpened] = useState<boolean>(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobile: boolean = useMediaQuery('(max-width:1279px)');
  const searchDebounce = useDebounce(searchValue, 400);
  const { searchData } = useSearch(searchDebounce);

  const handleSearch = (val: React.ChangeEvent<HTMLInputElement>) => {
    val.preventDefault();
    const [isValid] = isEnglishAlphabet(val.target.value);
    if (!isValid) {
      window.alert(FormErrorCode.Alphabet);
      return;
    }
    setSearchValue(val.target.value);
  };

  const handleKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setIsSearchResultOpened(false);
      if (searchValue) {
        handleViewAllResults();
      } else {
        window.location.href = `${config.mainPyramydairUrl}/search-results-ext`;
      }
    }
  };

  const handleClickOutside = () => {
    if (!mobile) {
      setIsSearchResultOpened(false);
    }
  };

  const handleOnFocus = () => {
    setIsSearchResultOpened(true);
    handleInputFocus(false);
  };

  const handleClearSearchResults = () => {
    setSearchValue('');
    setIsSearchResultOpened(false);
  };

  const handleSearchIconClick = () => {
    if (searchValue.length > 0) {
      if (!mobile) {
        handleViewAllResults();
        return;
      }
      handleClearSearchResults();
      searchInputRef.current?.blur();
      return;
    }
    setIsSearchResultOpened(true);
    if (searchValue.length == 0) {
      searchInputRef.current?.focus();
      return;
    }
    handleViewAllResults();
  };

  const handleViewAllResults = () => {
    const searchLocation =
      config.brandCode === '2'
        ? `${config.mainPyramydairUrl}/search.html?keyword=${searchValue}`
        : `${config.mainPyramydairUrl}/search-results-ext?keyword=${searchValue}`;
    window.location.href = searchLocation;
    handleClearSearchResults();
    searchInputRef.current?.blur();
  };

  useEffect(() => {
    handleSearchPosition();
  }, [mobile]);

  useEffect(() => {
    if (isMenuOpen && mobile) {
      setIsSearchResultOpened(false);
    }
  }, [isMenuOpen]);

  useClickOutSide(searchContainerRef, handleClickOutside);

  return (
    <div className={styles.search} ref={searchContainerRef}>
      <div className={styles.searchContainer}>
        <input
          id={id}
          data-testid={id}
          ref={searchInputRef}
          type="search"
          onChange={handleSearch}
          onFocus={handleOnFocus}
          value={searchValue}
          autoComplete="off"
          className={styles.searchInput}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onKeyDown={handleKeyDown}
          placeholder="Search PyramydAIR.com"
          name="search autocomplete"
        />
        <div
          id={`${id}-button`}
          data-testid={`${id}-button`}
          className={clsx(styles.searchIcon, {
            [styles.closeIcon]: searchValue.length > 0 && mobile,
          })}
          onClick={handleSearchIconClick}
        ></div>
        {searchData && isSearchResultOpened && searchValue.length > 2 && (
          <SearchResults
            searchResults={searchData}
            handleViewAllResults={handleViewAllResults}
            handleClearSearchResults={handleClearSearchResults}
            keyword={searchValue}
            isOpened={isSearchResultOpened}
            topPosition={topPosition}
            mobile={mobile}
          />
        )}
      </div>
    </div>
  );
};

export default memo(SearchComponent);
